import { BehaviorSubject, filter, Observable, Subject, take } from 'rxjs';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { environment } from '@environments/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranscriptStreamService } from './transcript/transcript-stream.service';

export enum WebsocketState {
  CONNECTING = 0,
  OPEN = 1,
  CLOSING = 2,
  CLOSED = 3,
}

export const RecognizerType = {
  TRANSCRIPTION: 'transcription',
  TRANSLATION: 'translation',
} as const;

export type RecognizerType = (typeof RecognizerType)[keyof typeof RecognizerType];

export interface SubtitleBoxSettings {
  position: string;
  size: string;
  background_color: string;
  background_color_opacity: number;
  overlay_color: string;
  overlay_color_opacity: number;
  max_visible_lines: number;
  font_size: number;
  font_color: string;
  font_color_opacity: number;
  font_family?: string;
  left_margin_px?: number;
  right_margin_px?: number;
  text_stroke_color?: string;
  text_stroke_width?: number;
  clear_transcripts_after_seconds?: number;
}

export interface DeviceSettings {
  appearance: {
    transcriptview: {
      subtitlebox: SubtitleBoxSettings;
    };
  };
  speech_recognizer: {
    recognition_language: string[];
    target_languages: string[];
  };
}

export interface DisplaySettingsResult {
  properties: {
    _name: string;
    _allowed_screen_modes: Array<{
      screen_width: number;
      screen_height: number;
      refresh_rates_hz: number[];
    }>;
  };
  configuration: {
    displayed_name: string;
    screen_mode: {
      screen_width: number;
      screen_height: number;
      refresh_rate_hz: number;
    };
    default_output_view: string;
    active_output_view: string;
    output_views: {
      full_screen?: {};
      overlay?: {
        appearance: {
          subtitlebox: SubtitleBoxSettings;
        };
        target_languages: string[];
        text_behavior?: {
          translation_buffer_ms?: number | null;
        };
      };
      qrview?: {};
      transcriptview?: {
        appearance: {
          subtitlebox: SubtitleBoxSettings;
        };
        target_languages: string[];
        text_behavior?: {
          translation_buffer_ms?: number | null;
        };
      };
    };
  };
}

export interface WebsocketPayload {
  event: WebsocketEventType | string;
  payload?: any;
}

export const WebsocketEvents = {
  PING: 'ping',
  PONG: 'pong',
  EDIT: 'edit',
  // speech sdk events
  SPEECH_SDK_SET_RECOGNITION_LANGUAGE: 'speech_sdk.set_recognition_language',
  SPEECH_SDK_RECOGNITION_LANGUAGE_UPDATED:
    'speech_sdk.recognition_language_updated',
  SPEECH_SDK_RECOGNIZER_TYPE_GET: 'speech_sdk.recognizer_type.get',
  SPEECH_SDK_RECOGNIZER_TYPE_RESULT: 'speech_sdk.recognizer_type.result',
  SPEECH_SDK_RECOGNIZER_TYPE_UPDATED: 'speech_sdk.recognizer_type.updated',
  SPEECH_SDK_RECOGNITION_LANGUAGE_GET: 'speech_sdk.recognition_language.get',
  SPEECH_SDK_RECOGNITION_LANGUAGE_RESULT: 'speech_sdk.recognition_language.result',
  // speech recognizer events
  SPEECH_RECOGNIZER_PHRASELIST_ADD: 'speech_recognizer.upadate_phrase_list',
  SPEECH_RECOGNIZER_UPDATE_TARGET_LANGUAGES:
    'speech_recognizer.update_target_languages',
  SPEECH_RECOGNIZER_TARGET_LANGUAGES_UPDATED:
    'speech_recognizer.target_languages.updated',
  // TODO: Pending to update
  // WARNING: The following events might change in the future
  // Dynamic display settings events - the display (HDMI-1 or HDMI-2) is passed dynamically
  DISPLAY_SETTINGS_GET: (display: string) => `display_settings.${display}.get`,
  DISPLAY_SETTINGS_RESULT: (display: string) => `display_settings.${display}.result`,
  DISPLAY_SETTINGS_SUBTITLEBOX_UPDATE: (display: string, outputView: string) =>
    `display_settings.${display}.configuration.output_views.${outputView}.appearance.subtitlebox.update`,
  DISPLAY_SETTINGS_SUBTITLEBOX_UPDATED: (display: string, outputView: string) =>
    `display_settings.${display}.configuration.output_views.${outputView}.appearance.subtitlebox_updated`,
  DISPLAY_SETTINGS_TARGET_LANGUAGES_UPDATE: (display: string, outputView: string) =>
    `display_settings.${display}.configuration.output_views.${outputView}.target_languages_updated`,
  DISPLAY_SETTINGS_TEXT_BEHAVIOR_UPDATED: (display: string, outputView: string) =>
    `display_settings.${display}.configuration.output_views.${outputView}.text_behavior_updated`,
  DEVICE_SETTINGS_STORE: 'device.settings.store',
  SPEECH_RECOGNITION_RESULT: 'speech.recognition.result',
  // SPEECH_RECOGNITION_PHRASELIST_ADD: 'phraselist.add',
  // SPEECH_RECOGNITION_LANGUAGE_INPUT_UPDATE: 'language.input.update',
  // SPEECH_RECOGNITION_LANGUAGE_OUTPUT_UPDATE: 'language.output.update',
  // DEVICE_SETTINGS_SPEECH_RECOGNITION_INPUT_LANGUAGE_UPDATE:
  //   'device.settings.speech_recognizer.input_language.update',
  // DEVICE_SETTINGS_SPEECH_RECOGNITION_OUTPUT_LANGUAGE_UPDATE:
  //   'device.settings.speech_recognizer.output_languages.update',
  TRANSCRIPTVIEW_SUBTITLEBOX_UPDATE:
    'device.settings.appearance.transcriptview.subtitlebox.update',
  // TODO: I'm lazy to update this one + is not in use yet... To be continued
  TRANSCRIPTVIEW_SUBTITLEBOX_UPDATED:
    'device.settings.appearance.transcriptview.subtitlebox.updated',
  NOT_IMPLEMENTED: 'not_implemented',
} as const;

export type WebsocketEventType =
  (typeof WebsocketEvents)[keyof typeof WebsocketEvents];

@Injectable({
  providedIn: 'root',
})
export class WebsocketService {
  private socket$: WebSocketSubject<WebsocketPayload> | null = null;
  private websocketServer: string = environment.speaksee.websocketUrl;
  private uuid = '';
  private jwt = '';
  private displayName = '';

  public incomingMessages = new Subject<any>();
  public outgoingMessageSubject = new Subject<WebsocketPayload>();

  private state = new BehaviorSubject<WebsocketState>(WebsocketState.CLOSED);
  public state$ = this.state.asObservable();

  private lastCloseCode = new BehaviorSubject<number | null>(null);
  public lastCloseCode$ = this.lastCloseCode.asObservable();
  private readonly _destroyRef = inject(DestroyRef);
  private readonly transcriptStream = inject(TranscriptStreamService);

  // Retry configuration
  private readonly MAX_RECONNECT_ATTEMPTS = 7;
  private reconnectAttempts = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private isManualDisconnect = false;
  private stableConnectionTimeout: ReturnType<typeof setTimeout> | null = null;

  private readonly closeEventCodes: { [key: number]: string } = {
    1000: 'Normal closure',
    1001: 'Going away',
    1002: 'Protocol error',
    1003: 'Unsupported data',
    1005: 'No status code present',
    1006: 'Abnormal closure',
    1007: 'Invalid frame payload data',
    1008: 'Policy violation',
    1009: 'Message too big',
    1010: 'Mandatory extension missing',
    1011: 'Internal server error',
    1015: 'TLS handshake failure',
  };

  get currentState() {
    return this.state.value;
  }

  private registerTranscriptStream(): void {
    if (this.uuid) {
      this.transcriptStream.register(this.uuid, this.incomingMessages);
    }
  }

  connect(uuid: string, jwt: string, displayName: string): void {
    if (!uuid) {
      throw new Error('UUID is required for WebSocket connection');
    }
    if (!jwt) {
      throw new Error('JWT token is required for WebSocket connection');
    }
    if (!displayName) {
      throw new Error('Display name is required for WebSocket connection');
    }

    this.uuid = uuid;
    this.jwt = jwt;
    this.displayName = displayName;
    this.isManualDisconnect = false; // Reset manual disconnect flag

    if (this.socket$) {
      console.warn('WebSocket is already connected or connecting');
      return;
    }

    this.socket$ = webSocket({
      url: `${this.websocketServer}?client_type=av_view&uuid=${this.uuid}&jwt=${this.jwt}&display_name=${this.displayName}`,
      openObserver: {
        next: () => {
          console.info('WebSocket connected');
          this.state.next(WebsocketState.OPEN);
          this.registerTranscriptStream();
          this.lastCloseCode.next(null);

          if (this.stableConnectionTimeout) {
            clearTimeout(this.stableConnectionTimeout);
          }
          this.stableConnectionTimeout = setTimeout(() => {
            this.reconnectAttempts = 0;
          }, 5000);
        },
      },
      closeObserver: {
        next: (event: CloseEvent) => {
          const reason = this.closeEventCodes[event.code] || 'Unknown reason';
          console.warn(
            `WebSocket closed: Code ${event.code}, Reason: ${reason}`
          );

          if (this.stableConnectionTimeout) {
            clearTimeout(this.stableConnectionTimeout);
            this.stableConnectionTimeout = null;
          }

          this.socket$ = null;
          this.state.next(WebsocketState.CLOSED);
          if (this.uuid) {
            this.transcriptStream.unregister(this.uuid);
          }
          this.lastCloseCode.next(event.code);

          // Don't reconnect on normal closure (1000), policy violation/JWT failure (1008), or manual disconnect
          if (event.code !== 1000 && event.code !== 1008 && !this.isManualDisconnect) {
            this.scheduleReconnect();
          }
        },
      },
    });

    this.socket$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (message) => this.handleIncomingMessage(message),
      error: (error) => {
        console.error('WebSocket error:', error);
      },
    });
  }

  /** Disconnect from the WebSocket server */
  disconnect(): void {
    if (!this.socket$) {
      console.warn('WebSocket is not connected');
      return;
    }

    this.isManualDisconnect = true;

    this.socket$.complete();
    this.socket$ = null;
    this.state.next(WebsocketState.CLOSED);

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.stableConnectionTimeout) {
      clearTimeout(this.stableConnectionTimeout);
      this.stableConnectionTimeout = null;
    }
    this.reconnectAttempts = 0;
    this.isManualDisconnect = false;

    console.info('WebSocket disconnected');
  }

  /** Schedule reconnection with exponential backoff */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.MAX_RECONNECT_ATTEMPTS) {
      console.error(`Max reconnection attempts (${this.MAX_RECONNECT_ATTEMPTS}) reached.`);
      return;
    }

    // Clear any existing timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    this.reconnectAttempts++;
    // Exponential backoff: 2^attempt seconds, capped at 60 seconds
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 60000);

    console.info(
      `Reconnection attempt ${this.reconnectAttempts}/${this.MAX_RECONNECT_ATTEMPTS} in ${delay}ms`
    );

    this.reconnectTimeout = setTimeout(() => {
      this.reconnect();
    }, delay);
  }

  /** Reconnect to the WebSocket server */
  private reconnect(): void {
    if (this.uuid && this.jwt && this.displayName) {
      this.connect(this.uuid, this.jwt, this.displayName);
    } else {
      console.error('Cannot reconnect: missing connection parameters');
    }
  }

  /** Update WebSocket server URL and reconnect */
  updateWebSocketServer(server: string): void {
    this.websocketServer = server;
    console.info(`WebSocket server updated to: ${this.websocketServer}`);

    this.disconnect();
    this.connect(this.uuid, this.jwt, this.displayName);
  }

  /** Start processing outgoing messages */
  start(): void {
    this.outgoingMessageSubject
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((message) => {
        if (this.socket$ && this.state.value === WebsocketState.OPEN) {
          this.socket$.next({
            event: message.event,
            payload: message.payload,
          });
        } else {
          console.warn('Cannot send message; WebSocket is not open');
        }
      });
  }

  /** Send a PONG message */
  public sendPong(): void {
    if (this.socket$ && this.state.value === WebsocketState.OPEN) {
      this.socket$.next({
        event: WebsocketEvents.PONG,
      });
    } else {
      console.warn('Cannot send PONG; WebSocket is not open');
    }
  }

  /** Handle incoming WebSocket messages */
  private handleIncomingMessage(message: WebsocketPayload): void {
    switch (message.event) {
      case WebsocketEvents.PING:
        this.sendPong();
        this.incomingMessages.next(message);
        break;
      default:
        this.incomingMessages.next(message);
        break;
    }
  }

  public waitForDeviceDetails(event: WebsocketEventType | string): Observable<any> {
    return this.incomingMessages.pipe(
      filter((message) => message.event === event),
      take(1)
    );
  }
}
